import clsx from 'clsx';
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { PredictSheetsIOExport, PredictSheetsIOImportSheetCreateResult } from '@futrli-sdk/predict';
import { INTERCOM_LAUNCHER_ID, StoreState, getValueFromQuery, workspaceByIdSelector } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { trackEvent } from '../../../../core/tracker';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';

import styles from './redirect-route.scss';
import { fetchForecastById } from '../../../../core/api/forecast';
import { getExportFilename, getImportFilename } from './utils';
import { PeriodType } from '../../../../core/constants';
import Button from 'carbon-react/lib/components/button';

interface Props {
  createSheet: (
    workspaceId: string,
    organisationId: string,
    forecastId: string,
    fileName: string
  ) => Promise<PredictSheetsIOImportSheetCreateResult | PredictSheetsIOExport>;
  loadingText: JSX.Element;
  trackingEvent: string;
  isExportRedirect?: boolean;
  periodType?: PeriodType;
}

/**
 * Route to automatically create either an export or import sheet while showing a spinner
 *
 * If the user hit this url at the end of an oauth flow (expected behaviour),
 * any errors will be provided as a query param `?error=some-oauth-error`. In
 * the case of errors we should not attempt an create an import sheet, but feed-back instead.
 */
export const RedirectRoute: React.FC<Props> = ({
  createSheet,
  loadingText,
  trackingEvent,
  isExportRedirect,
  periodType
}): JSX.Element => {
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;
  const { addErrorMessage } = useContext(PredictManagementContext);

  const location = useLocation();

  const intl = useIntl();

  const authError = getValueFromQuery(location, 'error');

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [createSheetError, setCreateSheetError] = useState<boolean>(false);

  const organisationName = useSelector((store: StoreState) => {
    const workspace = workspaceByIdSelector(store.normalised, workspaceId) as WorkspacesListingItem;
    const org = workspace?.organisations.find(o => o.id === organisationId);
    return org?.name;
  });

  useEffect(() => {
    const handleCreateImportSheet = async () => {
      try {
        trackEvent(trackingEvent);
        const forecast = await fetchForecastById(workspaceId, organisationId, forecastId as string);
        const createResult = await createSheet(
          workspaceId,
          organisationId,
          forecastId as string,
          isExportRedirect
            ? getExportFilename(organisationName || '', intl, forecast.name, forecast.kind, periodType || 'MONTHLY')
            : getImportFilename(organisationName || '', intl, forecast.name, forecast.kind)
        );
        createResult.sheet_url && window.open(createResult.sheet_url, '_self');
        setIsLoading(false);
      } catch {
        addErrorMessage?.(new Error('Failed to create sheet'));
        setCreateSheetError(true);
        setIsLoading(false);
      }
    };

    if (authError != undefined) {
      setIsLoading(false);
      return;
    }

    handleCreateImportSheet();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {isLoading && (
        <React.Fragment>
          <UILoader isFull={true} />
          <div className={styles.loadingTextWrapper}>
            <span className={styles.loadingText}>{loadingText}</span>
          </div>
        </React.Fragment>
      )}
      {createSheetError && (
        <ErrorMessage
          id="predict.error_creating_report"
          message={
            <FormattedMessage
              id="predict.error_creating_report"
              defaultMessage="There has been an issue creating your report. Please try again and contact support if it persists"
            />
          }
        />
      )}
      {authError &&
        (authError === 'access_denied' ? (
          <ErrorMessage
            id="predict.enable_access_to_google_sheets"
            message={
              <FormattedMessage
                id="predict.enable_access_to_google_sheets"
                defaultMessage="Please ensure you enable access to your Google Sheets account to create your import master sheet. You may close this tab and try again."
              />
            }
            excludeContactButton
          />
        ) : (
          <ErrorMessage
            id="predict.issue_authorising_google_sheets"
            message={
              <FormattedMessage
                id="predict.issue_authorising_google_sheets"
                defaultMessage="There has been an issue authorising access to your Google Sheets account. Please try again and contact support if it persists."
              />
            }
          />
        ))}
    </div>
  );
};

export const ErrorMessage: React.FC<{
  id: string;
  message: ReactElement;
  excludeContactButton?: boolean;
}> = ({ id, message, excludeContactButton = false }) => (
  <div className={styles.errorWrapper} data-testid={id}>
    <p className={clsx('tag is-danger is-light', styles.error)}>{message}</p>
    {!excludeContactButton && (
      <div>
        <Button mt={2} buttonType="primary" size="large" id={INTERCOM_LAUNCHER_ID}>
          <FormattedMessage id="common.contact_support" defaultMessage="Contact support" />
        </Button>
      </div>
    )}
  </div>
);
